import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { Menu, typographyStyles, Search } from '@livecommerce/design-system';
// import Search from 'components-v3/Search/index.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';

export const { getBodyText } = typographyStyles;

const MAX_ITEMS_SEEN = 7;
const LIST_ITEM_HEIGHT = 44;
const LOOKAHEAD_PADDING = 15;

const Container = styled.div`
  width: 100%;
  padding: 8px;
  color: #000;
`;

const OrganizationsListContainer = styled.ul`
  list-style-type: none;
  width: 100%;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: ${MAX_ITEMS_SEEN * LIST_ITEM_HEIGHT + LOOKAHEAD_PADDING}px;
  min-height: ${(props) => props.minListHeight}px;

  &::after {
    content: '';
    pointer-events: none;
    position: absolute;
    bottom: 8px;
    left: 0;
    right: 0;
    height: ${(props) => props.gradientHeight - 8}px;
    z-index: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.68) 50%,
      rgba(255, 255, 255, 0.84) 100%
    );
  }
`;

const organizationsListStyles = css`
  padding: 0;

  & button {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledSearch = styled(Search)`
  width: 100%;
`;

const isGradientShown = (numberOfOrganizations, scrollTop) => {
  const itemsOutOfBounds = numberOfOrganizations - MAX_ITEMS_SEEN;
  return itemsOutOfBounds * LIST_ITEM_HEIGHT - LOOKAHEAD_PADDING !== scrollTop;
};


const OrganizationsListV2 = forwardRef(({ isOpen, handleMenuItemClick, organizations, ...props }, ref) => {
  const { t } = useLocalization();
  const menuRef = useRef(null);
  const [showGradient, setShowGradient] = useState(true);
  const [searchInputValue, setSearchInputValue] = useState('');

  const filteredOrganizations = organizations.filter(({ title, id }) => {
    if (title.toLowerCase().includes(searchInputValue.toLowerCase())) return true;
    if (id.toLowerCase().includes(searchInputValue.toLowerCase())) return true;
    return false;
  });

  useEffect(() => {
    const currentRef = menuRef.current;

    const listener = currentRef?.addEventListener('scroll', (event) => {
      const scrollTop = event.target.scrollTop;
      const gradientIsShown = isGradientShown(filteredOrganizations.length, scrollTop);
      setShowGradient(gradientIsShown);
    });

    return () => {
      currentRef?.removeEventListener('scroll', listener);
    };
  }, [filteredOrganizations.length]);

  const handleChange = (event) => {
    const searchString = event.target.value;
    if (searchString) {
      trackingService.track({
        eventType: 'org-switcher:search',
        value: encodeURIComponent(searchString),
      });
    }
    setSearchInputValue(searchString);
  };

  const gradientHeight =
    showGradient && filteredOrganizations.length > MAX_ITEMS_SEEN ? LIST_ITEM_HEIGHT * 2 + LOOKAHEAD_PADDING : 0;

  const minListHeight =
    organizations.length >= MAX_ITEMS_SEEN ? MAX_ITEMS_SEEN * LIST_ITEM_HEIGHT + LOOKAHEAD_PADDING : 0;

  const filteredOrganizationsItems = filteredOrganizations.map((org) => {
    return { key: org?.id, label: org?.title, action: (e) => handleMenuItemClick(org, e) };
  });

  return (
    <Container ref={ref} {...props}>
      {organizations.length >= MAX_ITEMS_SEEN && (
        <StyledSearch
          placeholder={t('genericLabels.searchPlaceholder')}
          value={searchInputValue}
          onChange={handleChange}
          isFixed
        />
      )}
      <OrganizationsListContainer ref={menuRef} gradientHeight={gradientHeight} minListHeight={minListHeight}>
        <Menu
          items={filteredOrganizationsItems}
          className={organizationsListStyles}
          minWidth={0}
        />
      </OrganizationsListContainer>
    </Container>
  );
});

export default OrganizationsListV2;
